import { Injectable, inject } from '@angular/core';

import {
    DisplayType,
    Participant,
    ParticipantMarketType,
    ParticipantPriceStatus,
    ParticipantPriceType,
    ParticipantStatus,
    ParticipantType,
} from '@cds/betting-offer';
import { FixturePricingState } from '@cds/betting-offer/add-ons';
import { EasyBetView } from '@cds/betting-offer/domain-specific/horse-racing/racing-post';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { RacingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { toDictionary } from '@frontend/sports/common/core/utils/collection';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { translationToStr } from '@frontend/sports/common/core/utils/translations';
import {
    EasyBetViewStats,
    HorseParticipantInfo,
    ParticipantFixture,
    WinGameFixture,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';
import { EasyBetViewOption } from '@frontend/sports/types/components/features';
import { RacingTerminal } from '@frontend/sports/types/components/sitecore';

import { V2OptionMarketXCastPickId, V2ParticipantPickId } from '../../../betslip/core/picks/pick-id';
import { ParticipantPickType } from '../../../betslip/core/picks/pick-models';
import { HorsesFixturePricingHelper } from './horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from './horses-fixture-sorting-helper.service';

@Injectable({ providedIn: 'root' })
export class HorsesFixtureParticipantHelper {
    private horsesFixturePricingHelper = inject(HorsesFixturePricingHelper);
    private horsesFixtureSortingHelper = inject(HorsesFixtureSortingHelper);
    private racingConfig = inject(RacingConfig);
    private sitecore = inject(Sitecore);

    /**
     * Formats comma separated list of fixture participants
     *
     * @param fixture Horses fixture
     */
    getCombinedNonRunners(fixture: WinGameFixture, isGreyhounds = false): string {
        if (fixture.isOptionBet && isGreyhounds) {
            let d = this.getNonRunnersList(fixture);
            d = this.removeTrapNonrunners(d);
            return d
                .map((nonrunner) => {
                    if (nonrunner.properties?.type !== ParticipantType.Trap && nonrunner.properties?.associatedFixtureParticipantId !== undefined) {
                        return `${fixture.participants.find((fp) => fp.id === nonrunner.properties?.associatedFixtureParticipantId)!.participantId} ${translationToStr(nonrunner.name)}`;
                    } else {
                        return `${nonrunner.participantId} ${this.sitecore.greyhoundRacing.messages.VacantGreyHound}`;
                    }
                })
                .join(', ');
        }

        return this.getNonRunnersList(fixture)
            .sort((a, b) => (a.properties!.raceCardNumber || 0) - (b.properties!.raceCardNumber || 0))
            .map((fp) => `${fp.properties!.raceCardNumber} ${translationToStr(fp.name)}`)
            .join(', ');
    }

    removeTrapNonrunners(participants: WinGameParticipant[]): WinGameParticipant[] {
        participants.forEach((p) => {
            let nonrunner = participants.find((x) => x.id === p.properties?.associatedFixtureParticipantId);

            if (nonrunner?.properties?.type === ParticipantType.Trap) {
                participants.splice(participants.indexOf(nonrunner), 1);
            }
        });
        return participants;
    }

    isNonRunner(participant: WinGameParticipant): boolean {
        return participant.status === ParticipantStatus.NonRunner || participant.status === ParticipantStatus.Withdrawn;
    }

    /**
     * Fixture participants non runners
     *
     * @param fixture Horses fixture
     */
    getNonRunnersList<TParticipant extends WinGameParticipant, TFixture extends ParticipantFixture<TParticipant>>(fixture: TFixture): TParticipant[] {
        return fixture.participants.filter((fp) => this.isNonRunner(fp));
    }

    getRunnersCount(fixture: WinGameFixture): number {
        return fixture.participants
            .filter(
                (fp) =>
                    fp.properties!.type !== ParticipantType.Favourite &&
                    fp.status !== ParticipantStatus.NonRunner &&
                    fp.status !== ParticipantStatus.Withdrawn &&
                    fp.status !== ParticipantStatus.ReservedRunner,
            )
            .filter((x) => x.properties!.type === ParticipantType.Horse || x.properties!.type === ParticipantType.Trap).length;
    }

    getPlacedParticipants(fixture: WinGameFixture): WinGameParticipant[] {
        let participants = fixture.participants
            .filter((p) => !this.isNonRunner(p) && p.properties!.type !== ParticipantType.Favourite)
            .sort((prev, next) => (prev.properties!.rank || Number.MAX_VALUE) - (next.properties!.rank || Number.MAX_VALUE));

        return participants;
    }

    /**
     * Returns participants that have status NotDefined or Finished.
     * It also checks that at least some of the prices are not Hidden.
     *
     * @param {Participant[]} participants
     * @param {MarketType} marketType
     * @returns {Participant[]}
     * @memberof HorsesFixtureParticipantHelper
     */

    getParticipants<TParticipant extends Participant>(participants: TParticipant[]): TParticipant[] {
        return participants.filter(
            (fp) =>
                (fp.status === ParticipantStatus.NotDefined || fp.status === ParticipantStatus.Finished) &&
                (!fp.options.length || fp.options.some((option) => option.participantPriceStatus !== ParticipantPriceStatus.Hidden)),
        );
    }

    /**
     * Returns favourite horses for resulted race based on number of Favorites defined for fixture (0, 1 or 2)
     * If race is not resulted, returns null
     *
     * @param fixture Horses fixture
     */

    getResultedFavourites<TParticipant extends WinGameParticipant, TFixture extends ParticipantFixture<TParticipant>>(
        fixture: TFixture,
    ): TParticipant[] {
        if (fixture.addons.pricingState !== FixturePricingState.Resulted && !fixture.addons.isResulted) {
            return [];
        }

        const finishedParticipants = fixture.participants.filter((p) => p.status === ParticipantStatus.Finished);
        const prices = this.horsesFixturePricingHelper.getPrices(fixture, ParticipantMarketType.Win, ParticipantPriceType.Fixed);
        const sortedByPrice = this.horsesFixtureSortingHelper.sortParticipants(finishedParticipants, prices);
        const favoriteCount = fixture.participants.filter((p) => p.properties!.type === ParticipantType.Favourite).length;

        return sortedByPrice.slice(0, favoriteCount);
    }

    favouriteResultedOptionParticipants(fixture: WinGameFixture): WinGameParticipant[] {
        const optionGroups = fixture.optionGroups.filter((t) => t.detailedGrouping.displayType === DisplayType.Wincast);
        const finishedParticipants = fixture.participants.filter((p) => p.status === ParticipantStatus.Finished);
        const options = this.horsesFixturePricingHelper.getOptionPrices(fixture, optionGroups[0], ParticipantPriceType.Fixed);
        const sortedByPrice = this.horsesFixtureSortingHelper.sortV3Participants(finishedParticipants, options);
        const favoriteCount = fixture.participants.filter((p) => p.properties!.type === ParticipantType.Favourite).length;

        return this.horsesFixtureSortingHelper.getFavouriteParticipantWithNames(sortedByPrice, favoriteCount);
    }

    /**
     * True if at least one fixture participant has silk url available, otherwise false
     *
     * @param fixture Fixture to check
     */
    areSilksAvailable(fixture: WinGameFixture): boolean {
        return fixture.participants.some((fp) => (fp.properties!.silk && fp.properties!.silk.length > 0) || false);
    }

    getHorseRaceInfo(participant: WinGameParticipant): HorseParticipantInfo {
        return {
            raceCardNumber: participant.properties!.raceCardNumber,
            drawNumber: participant.properties!.drawNumber,
            jockey: participant.properties!.jockey || participant.timeformInfo?.jockeyName || '',
            trainer: participant.properties!.trainer || participant.timeformInfo?.trainerName || '',
            silkUrl: participant.timeformInfo?.silkUrl || '',
            silkDescription: participant.timeformInfo?.silkDescription || '',
            isFavorite: participant.properties!.type === ParticipantType.Favourite,
            ratingStars: participant.timeformInfo?.ratingStars,
        };
    }

    buildParticipantRecentResults(participants: WinGameParticipant[], isGreyHoundsRacing?: boolean): NumberDictionary<string[]> {
        const participantForm: NumberDictionary<string[]> = [];
        const lowerResult = 0;
        const thirdResult = 3;
        const allowedLimit = 5;

        participants.map((p) => {
            const racingForm = isGreyHoundsRacing ? p.racingPostInfo?.greyhoundsData?.form : p.racingPostInfo?.horseData?.form;
            if (racingForm && racingForm !== '-') {
                const recentResults = racingForm
                    .split('')
                    .filter((form) => Number(form) !== lowerResult)
                    .map((s) => {
                        if (isNaN(Number(s))) {
                            return s;
                        } else if (Number(s) <= thirdResult) {
                            //below cast is added just to avoid object index issues
                            return this.sitecore.horseRacing.common[Number(s) as unknown as '1'];
                        } else {
                            return `${Number(s) + this.sitecore.horseRacing.common.nth}`;
                        }
                    });

                if (recentResults.length > allowedLimit) {
                    recentResults.splice(lowerResult, recentResults.length - allowedLimit);
                }
                participantForm[p.id] = recentResults;
            } else {
                participantForm[p.id] = [];
            }
        });

        return participantForm;
    }

    getEasyBetViewData(
        easyBetViewOptions: EasyBetView[],
        isGreyhounds: boolean = false,
    ): {
        [attributeName: string]: EasyBetViewStats;
    } {
        const runnerStats: { [attributeName: string]: EasyBetViewStats } = {};
        easyBetViewOptions?.forEach((x) => {
            runnerStats[x.easyBetViewKey] = this.easyBetWayMapping(x.easyBetViewValue, isGreyhounds);
        });

        return runnerStats;
    }

    easyBetWayMapping(easyBetViewValue: number, isGreyhounds: boolean = false): EasyBetViewStats {
        if (isGreyhounds) {
            const mappedValue = this.racingConfig.greyhoundsEasyBetViewOptions.find((x) => this.checkRange(easyBetViewValue, x));

            return { color: mappedValue!.color, grade: mappedValue!.grade };
        } else {
            const horseRacingMappedValue = this.racingConfig.easyBetViewOptions.find((x) => this.checkRange(easyBetViewValue, x));

            return {
                color: horseRacingMappedValue!.color,
                label: this.sitecore.horseRacing.racingTerminal[horseRacingMappedValue!.siteCoreText as keyof RacingTerminal],
            };
        }
    }

    private checkRange(actualValue: number, easyBetViewOption: EasyBetViewOption): boolean {
        if (easyBetViewOption.lowerLimit && easyBetViewOption.upperLimit) {
            return easyBetViewOption.lowerLimit <= actualValue && actualValue < easyBetViewOption.upperLimit;
        } else if (easyBetViewOption.lowerLimit) {
            return easyBetViewOption.lowerLimit <= actualValue;
        } else if (easyBetViewOption.upperLimit) {
            return actualValue < easyBetViewOption.upperLimit;
        }

        return false;
    }

    checkForTopRating<T extends WinGameParticipant>(participants: T[]): T[] {
        const maxRating = Math.max(...participants.map((p) => p.racingPostInfo?.horseData?.starRating || 0));
        participants.forEach((p) => {
            if (p.racingPostInfo?.horseData) {
                p.racingPostInfo.horseData.hasTopStarRating = maxRating > 0 ? p.racingPostInfo.horseData?.starRating === maxRating : false;
            }
        });

        return participants;
    }

    getParticipantOptionGroups(
        optionGroup: EventOptionGroup,
        participants: WinGameParticipant[],
    ): {
        [id: number]: EventOption[];
    } {
        const participantOptionGroups: { [id: number]: EventOption[] } = {};

        optionGroup.options.forEach((op: EventOption) => {
            const participant = participants.find((x) => x.id === op.optionPlayerId);
            if (participant) {
                if (!participantOptionGroups[participant.id]) {
                    participantOptionGroups[participant.id] = [];
                }
                op.online = this.horsesFixturePricingHelper.isV3WinOptionAvailable(op);

                participantOptionGroups[participant.id].push(op);
            }
        });

        return participantOptionGroups;
    }

    buildWinParticipantOptionGroups(fixture: WinGameFixture): { [id: string]: EventOption[] } {
        let participantOptionGroups = {};
        const participants = this.getParticipants(fixture.participants);
        const optionGroups = fixture.optionGroups.filter((t) => t.detailedGrouping.displayType === DisplayType.Wincast);
        if (optionGroups && optionGroups.length) {
            participantOptionGroups = this.getParticipantOptionGroups(optionGroups[0], participants);
        }

        return participantOptionGroups;
    }

    createOptionPick(
        fixture: WinGameFixture,
        participants: WinGameParticipant[],
        marketType: ParticipantMarketType,
        betType: ParticipantPickType,
        participantOptionGroups: { [id: string]: EventOption[] },
        optionGroup?: EventOptionGroup,
    ): V2OptionMarketXCastPickId | void {
        const marketId = optionGroup?.name === marketType ? Number(optionGroup?.id) : null;
        if (marketId == null) {
            return;
        }
        const optionsOrder = toDictionary(
            participants,
            (p) => participantOptionGroups[p.id][0].id,
            (_, index) => index + 1,
        );
        const priceOrder = toDictionary(
            participants,
            (p) => participantOptionGroups[p.id][0].priceId!,
            (_, index) => index + 1,
        );

        return new V2OptionMarketXCastPickId(fixture.id, marketId, optionsOrder, priceOrder, betType);
    }

    createParticipantPick(
        fixture: WinGameFixture,
        participants: WinGameParticipant[],
        marketType: ParticipantMarketType,
        betType: ParticipantPickType,
    ): V2ParticipantPickId | void {
        const marketId = this.getMarketId(participants, marketType);
        if (marketId == null) {
            return;
        }
        const participantOrder = toDictionary(
            participants,
            (p) => p.id,
            (_, index) => index + 1,
        );

        return new V2ParticipantPickId(fixture.id, marketId, participantOrder, betType);
    }

    private getMarketId(participants: WinGameParticipant[], marketType: ParticipantMarketType): number | undefined {
        return participants
            .flatMap((p) => p.options)
            .filter((o) => o.marketType === marketType)
            .map((o) => o.marketId)[0];
    }

    getOptionMarketPlaceTerms(optionGroup: EventOptionGroup): number | null {
        const placeterm = {
            isEachWay: optionGroup.isEachWay || false,
            placeTerms: optionGroup.placeTerms,
        };

        if (!placeterm?.isEachWay) {
            return null;
        }

        return placeterm.placeTerms ? placeterm.placeTerms.places : null;
    }
}
